<template>
  <div class="page-shell">
    <div class="dash-hero">
      <div class="dash-hero__text">
        <h1 class="dash-hero__title">
          Welcome back, {{ user.username }}
          <span class="dash-hero__wave">👋</span>
        </h1>
        <p class="dash-hero__subtitle">
          Here's an overview of your biometric system today.
        </p>
      </div>
      <v-icon size="88" class="dash-hero__icon">mdi-fingerprint</v-icon>
    </div>

    <v-row class="mt-2">
      <v-col
        cols="12"
        sm="6"
        md="3"
        v-for="(stat, i) in stats"
        :key="stat.label"
      >
        <div
          class="stat-card"
          :style="{ animationDelay: i * 0.08 + 's' }"
          @click="$router.push(stat.path)"
        >
          <v-avatar size="44" :color="stat.bg" class="stat-card__avatar">
            <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
          </v-avatar>
          <div class="stat-card__info">
            <div class="stat-card__value">{{ stat.value }}</div>
            <div class="stat-card__label">{{ stat.label }}</div>
          </div>
          <v-icon small class="stat-card__arrow">mdi-arrow-top-right</v-icon>
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12" md="7">
        <div class="content-card fill-height">
          <div class="d-flex align-center mb-4">
            <h3 class="dash-section-title">
              <v-icon color="primary" class="mr-2"
                >mdi-clock-time-four-outline</v-icon
              >
              Recent Activity
            </h3>
            <v-spacer></v-spacer>
            <v-btn text small color="primary" to="/attendances">
              View all
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>

          <div v-if="recentAttendances.length">
            <div
              v-for="entry in recentAttendances"
              :key="entry.id"
              class="activity-row"
            >
              <v-avatar size="34" color="#EEF2FF" class="mr-3">
                <span class="activity-row__initials">
                  {{ initials(entry) }}
                </span>
              </v-avatar>
              <div class="activity-row__info">
                <div class="activity-row__name">
                  {{ entry.employee ? entry.employee.name : "Unknown" }}
                </div>
                <div class="activity-row__time">{{ entry.timestamp }}</div>
              </div>
              <v-chip
                x-small
                outlined
                :color="entry.type == 1 ? 'error' : 'primary'"
              >
                {{ entry.type == 1 ? "Out" : "In" }}
              </v-chip>
            </div>
          </div>
          <div v-else class="dash-empty">
            <v-icon size="40" color="grey lighten-1"
              >mdi-calendar-blank-outline</v-icon
            >
            <p>No attendance records yet. Import from your device to begin.</p>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="5">
        <div class="content-card fill-height">
          <h3 class="dash-section-title mb-4">
            <v-icon color="primary" class="mr-2"
              >mdi-lightning-bolt-outline</v-icon
            >
            Quick Actions
          </h3>
          <div
            v-for="action in quickActions"
            :key="action.title"
            class="quick-action"
            @click="$router.push(action.path)"
          >
            <v-avatar size="38" :color="action.bg" class="mr-3">
              <v-icon small :color="action.color">{{ action.icon }}</v-icon>
            </v-avatar>
            <div class="quick-action__info">
              <div class="quick-action__title">{{ action.title }}</div>
              <div class="quick-action__desc">{{ action.desc }}</div>
            </div>
            <v-icon small color="grey">mdi-chevron-right</v-icon>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "DashboardView",
  computed: {
    ...mapGetters({
      user: "auth/user",
      employees: "employee/employees",
      departments: "department/departments",
      devices: "device/devices",
      all_attendances: "attendance/all_attendances",
    }),
    stats() {
      return [
        {
          label: "Employees",
          value: this.employees.length,
          icon: "mdi-account-multiple-outline",
          color: "#6366F1",
          bg: "#EEF2FF",
          path: "/employees",
        },
        {
          label: "Departments",
          value: this.departments.length,
          icon: "mdi-domain",
          color: "#8B5CF6",
          bg: "#F5F3FF",
          path: "/departments",
        },
        {
          label: "Devices",
          value: this.devices.length,
          icon: "mdi-devices",
          color: "#0EA5E9",
          bg: "#F0F9FF",
          path: "/device_info",
        },
        {
          label: "Attendance Records",
          value: this.all_attendances.length,
          icon: "mdi-book-open-variant",
          color: "#10B981",
          bg: "#ECFDF5",
          path: "/attendances",
        },
      ];
    },
    recentAttendances() {
      return [...this.all_attendances]
        .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)))
        .slice(0, 7);
    },
    quickActions() {
      return [
        {
          title: "Import Attendance",
          desc: "Pull the latest logs from your biometric device",
          icon: "mdi-download-outline",
          color: "#6366F1",
          bg: "#EEF2FF",
          path: "/attendances",
        },
        {
          title: "Manage Employees",
          desc: "Add, edit, or review employee records",
          icon: "mdi-account-plus-outline",
          color: "#8B5CF6",
          bg: "#F5F3FF",
          path: "/employees",
        },
        {
          title: "Leave Credits",
          desc: "Track vacation and sick leave balances",
          icon: "mdi-calendar-check-outline",
          color: "#10B981",
          bg: "#ECFDF5",
          path: "/leave_credits",
        },
        {
          title: "Register a Device",
          desc: "Connect a new ZKTeco / NGTeco device",
          icon: "mdi-plus-network-outline",
          color: "#0EA5E9",
          bg: "#F0F9FF",
          path: "/device_info",
        },
      ];
    },
  },
  methods: {
    ...mapActions({
      get_employees: "employee/get_employees",
      get_departments: "department/get_departments",
      get_devices: "device/get_devices",
      get_all_attendances: "attendance/get_all_attendances",
    }),
    initials(entry) {
      const name = (entry.employee && entry.employee.name) || "?";
      return name
        .split(" ")
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
  },
  async created() {
    if (this.employees.length <= 0) this.get_employees();
    if (this.departments.length <= 0) this.get_departments(this.user.id);
    if (this.devices.length <= 0) this.get_devices();
    if (this.all_attendances.length <= 0) this.get_all_attendances();
  },
};
</script>

<style scoped>
.dash-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(120deg, #0f172a 0%, #312e81 60%, #4c1d95 100%);
  border-radius: 20px;
  padding: 32px;
  color: #ffffff;
  box-shadow: 0 12px 32px rgba(49, 46, 129, 0.35);
  animation: fade-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.dash-hero__title {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.dash-hero__wave {
  display: inline-block;
  animation: wave 1.6s ease-in-out 0.5s 2;
  transform-origin: 70% 70%;
}
.dash-hero__subtitle {
  margin: 6px 0 0;
  font-size: 0.92rem;
  color: rgba(226, 232, 240, 0.75);
}
.dash-hero__icon {
  color: rgba(139, 92, 246, 0.35) !important;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 16px rgba(15, 23, 42, 0.06);
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.22s cubic-bezier(0.22, 1, 0.36, 1);
  animation: fade-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
.stat-card:hover .stat-card__arrow {
  opacity: 1;
  transform: translate(0, 0);
}
.stat-card__value {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.1;
}
.stat-card__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
}
.stat-card__arrow {
  margin-left: auto;
  opacity: 0;
  transform: translate(-4px, 4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dash-section-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.activity-row {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 10px;
  transition: background-color 0.15s ease;
}
.activity-row:hover {
  background: #f8fafc;
}
.activity-row__initials {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6366f1;
}
.activity-row__info {
  flex: 1;
  min-width: 0;
}
.activity-row__name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.activity-row__time {
  font-size: 0.72rem;
  color: #94a3b8;
}

.quick-action {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}
.quick-action:hover {
  border-color: #c7d2fe;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.12);
  transform: translateX(3px);
}
.quick-action__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0f172a;
}
.quick-action__desc {
  font-size: 0.72rem;
  color: #64748b;
}
.quick-action__info {
  flex: 1;
}

.dash-empty {
  text-align: center;
  padding: 40px 16px;
  color: #94a3b8;
  font-size: 0.85rem;
}
.dash-empty p {
  margin-top: 12px;
}

@keyframes fade-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes wave {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(16deg);
  }
  75% {
    transform: rotate(-8deg);
  }
}
</style>
